import { useIntl } from 'react-intl';

import { useUserTimezone } from '@/shared/hooks';
import { parseDateAsLocal } from '@/shared/lib';

import {
  ADULT_AGE,
  MONTHS_IN_YEAR,
  ONE_YEAR,
  SHOW_AGE_IN_MONTHS_THRESHOLD,
} from '../config';

type Options = {
  patientDateOfBirth: string | undefined;
  onlyAge?: boolean;
  pluralized?: boolean;
};

const calculateYearDifference = (date1: Date, date2: Date) => {
  const yearDiff = date1.getFullYear() - date2.getFullYear();
  const monthDiff = date1.getMonth() - date2.getMonth();
  const dayDiff = date1.getDate() - date2.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    return yearDiff - 1;
  }
  return yearDiff;
};

const calculateMonthDifference = (date1: Date, date2: Date) => {
  const yearDiff = date1.getFullYear() - date2.getFullYear();
  const monthDiff = date1.getMonth() - date2.getMonth();
  const dayDiff = date1.getDate() - date2.getDate();

  const months = yearDiff * 12 + monthDiff;

  return dayDiff < 0 ? months - 1 : months;
};

export const useFormatPatientDateOfBirth = (options: Options) => {
  const { patientDateOfBirth, pluralized = false, onlyAge = false } = options;

  const { formatMessage, formatDate } = useIntl();

  const userTimezone = useUserTimezone();

  if (!patientDateOfBirth) {
    return '';
  }

  const dateOfBirth = formatDate(parseDateAsLocal(patientDateOfBirth), {
    dateStyle: 'long',
    timeZone: userTimezone,
  });

  const ageInYears = calculateYearDifference(
    new Date(),
    parseDateAsLocal(patientDateOfBirth),
  );

  const isChild = ageInYears <= SHOW_AGE_IN_MONTHS_THRESHOLD;

  const ageInMonths = calculateMonthDifference(
    new Date(),
    parseDateAsLocal(patientDateOfBirth),
  );

  const pluralizedMonth = formatMessage(
    {
      id: 'global.ageInMonth',
      defaultMessage: '{age, plural, one {# month} other {# months}}',
    },
    { age: ageInMonths },
  );

  const age = isChild
    ? pluralizedMonth
    : formatMessage(
        {
          id: 'global.age',
          defaultMessage: '{age, plural, one {# y.o.} other {# y.o.}}',
        },
        { age: ageInYears },
      );

  if (pluralized) {
    const formattedAgeInYears = formatMessage(
      {
        id: 'global.ageInYear',
        defaultMessage: '{age, plural, one {# year} other {# years}}',
      },
      { age: ageInYears },
    );

    if (ageInYears >= ADULT_AGE) {
      return formattedAgeInYears;
    }

    if (ageInYears >= ONE_YEAR) {
      const remainingMonths = ageInMonths - ageInYears * MONTHS_IN_YEAR;

      const formattedMonths = remainingMonths
        ? formatMessage(
            {
              id: 'global.ageInMonth',
              defaultMessage: '{age, plural, one {# month} other {# months}}',
            },
            { age: remainingMonths },
          )
        : '';

      const comma = remainingMonths ? ',' : '';

      return `${formattedAgeInYears}${comma} ${formattedMonths}`;
    }

    return pluralizedMonth;
  }

  return onlyAge ? age : `${dateOfBirth}, ${age}`;
};
